import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './StatisticalCard.css';

/**
 * StatisticalCard Component
 * Displays a statistical data point with title, value, and description
 *
 * @param {string} title - The card title
 * @param {string|number} value - The statistical value to display
 * @param {string} description - Additional descriptive text
 * @param {string} bgColor - Background color for the card
 * @param {boolean} showBadge - Whether to show the badge
 * @param {string} hoverTitle - Title to show on hover
 * @param {string} hoverDescription - Description to show on hover
 */
function StatisticalCard({
    title,
    value,
    description,
    bgColor,
    showBadge,
    hoverTitle,
    hoverDescription,
}) {
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        setIsMobile(mediaQuery.matches);

        const handleResize = (e) => {
            setIsMobile(e.matches);
        };

        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
    }, []);

    const handleClick = () => {
        if (isMobile) {
            setIsHovered((prev) => !prev);
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // Map color names to CSS variables
    const colorMap = {
        'spotlight-yellow': 'var(--spotlight-yellow)',
        'spotlight-primary': 'var(--spotlight-primary)',
        'spotlight-dark': 'var(--spotlight-dark)',
    };

    const cardClassName = `statistical-card text-dark border-0 h-100 ${isMobile ? 'stat-card-mobile' : 'stat-card-desktop'}`;

    const cardProps = {
        className: cardClassName,
        style: { '--card-bg-color': colorMap[bgColor] || bgColor },
    };

    if (isMobile) {
        cardProps.onClick = handleClick;
    } else {
        cardProps.onMouseEnter = handleMouseEnter;
        cardProps.onMouseLeave = handleMouseLeave;
    }

    // Extract simple label for mobile from description
    const getMobileLabel = () => {
        const words = description.toLowerCase().split(' ');
        if (words.includes('conversations')) return 'conversations';
        if (words.includes('datasets')) return 'datasets';
        if (words.includes('communities')) return 'communities';
        return words[0]; // fallback to first word
    };

    return (
        <Card {...cardProps}>
            <Card.Body
                className={`d-flex flex-column p-4 position-relative ${isMobile ? 'justify-content-center align-items-start text-start' : 'justify-content-start'}`}
            >
                {isMobile ? (
                    /* Mobile: Simple display with value + label */
                    <>
                        <Card.Text className="display-3 fw-bold mb-1 text-dark">
                            {value}
                        </Card.Text>
                        <Card.Text className="fs-6 mb-0 text-dark">
                            {getMobileLabel()}
                        </Card.Text>
                    </>
                ) : (
                    /* Desktop: Full interactive display */
                    <>
                        {showBadge && (
                            <>
                                {/* Top left badge */}
                                <div className="badge text-start mb-3 fs-5 stat-badge">
                                    <span
                                        className={`stat-badge-text ${isHovered ? 'hovered' : ''}`}
                                    >
                                        {title}
                                    </span>
                                    {isHovered && (
                                        <span className="stat-badge-hover-text">
                                            {hoverTitle}
                                        </span>
                                    )}
                                </div>

                                {/* Top right icon */}
                                <div className="stat-icon-container">
                                    {/* Plus icon - slides out */}
                                    <div
                                        className={`stat-plus-icon ${isHovered ? 'hovered' : ''}`}
                                    >
                                        <div className="stat-plus-horizontal" />
                                        <div className="stat-plus-vertical" />
                                    </div>

                                    {/* Minus icon - slides in */}
                                    {isHovered && (
                                        <div className="stat-minus-icon" />
                                    )}
                                </div>

                                {/* Large value - slide out on hover */}
                                <Card.Text
                                    className={`fw-bold mb-3 stat-value ${isHovered ? 'hovered' : ''}`}
                                >
                                    {value}
                                </Card.Text>

                                {/* Description - slide out and slide in */}
                                <div className="stat-description-container">
                                    <Card.Text
                                        className={`mb-0 fs-5 stat-description ${isHovered ? 'hovered' : ''}`}
                                    >
                                        {description}
                                    </Card.Text>
                                    {isHovered && (
                                        <Card.Text className="mb-0 fs-5 stat-hover-description">
                                            {hoverDescription}
                                        </Card.Text>
                                    )}
                                </div>
                            </>
                        )}

                        {!showBadge && (
                            <>
                                <Card.Title className="fs-5 fw-semibold mb-3 text-dark">
                                    {title}
                                </Card.Title>
                                <Card.Text className="display-4 fw-bold mb-3 text-dark">
                                    {value}
                                </Card.Text>
                                <Card.Text className="text-dark mb-0">
                                    {description}
                                </Card.Text>
                            </>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
}

StatisticalCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string.isRequired,
    bgColor: PropTypes.string.isRequired,
    showBadge: PropTypes.bool,
    hoverTitle: PropTypes.string,
    hoverDescription: PropTypes.string,
};

export default StatisticalCard;
